import React, { Suspense, startTransition } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Loader2 } from 'lucide-react';
import { useSafeTranslation } from '@/hooks/useSafeTranslation';

interface LazyWrapperProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  errorFallback?: React.ComponentType<{ error: Error; resetErrorBoundary: () => void }>;
  minLoadingTime?: number;
}

const DefaultLoadingFallback = () => {
  const { t } = useSafeTranslation();

  return (
(
  <div className="flex items-center justify-center h-64">
    <div className="text-center">
      <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-blue-600" />
      <p className="text-gray-600">{t('ui.loading')}</p>
    </div>
  </div>
)
  );
};

const DefaultErrorFallback: React.FC<{ error: Error; resetErrorBoundary: () => void }> = ({ 
  error, 
  resetErrorBoundary 
}) => {
  const { t } = useSafeTranslation();
  let errorMessage = 'An unknown error occurred';
  
  try {
    if (error?.message && typeof error.message === 'string') {
      errorMessage = error.message;
    } else if (error && typeof error === 'string') {
      errorMessage = error;
    } else if (error) {
      errorMessage = 'Component failed to load';
    }
  } catch {
    errorMessage = 'Component failed to load';
  }
  
  return (
    <div className="flex items-center justify-center h-64">
      <div className="text-center p-6 bg-red-50 rounded-lg border border-red-200">
        <h3 className="text-lg font-semibold text-red-800 mb-2">{t('ui.somethingWentWrong')}</h3>
        <p className="text-red-600 mb-4">{errorMessage}</p>
        <button
          onClick={resetErrorBoundary}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          {t('ui.tryAgain')}
        </button>
      </div>
    </div>
  );
};

const LazyWrapper: React.FC<LazyWrapperProps> = ({
  children,
  fallback = <DefaultLoadingFallback />,
  errorFallback = DefaultErrorFallback,
  minLoadingTime = 200,
}) => {
  return (
    <ErrorBoundary
      FallbackComponent={errorFallback}
      onError={(error, errorInfo) => {
        try {
          console.error('LazyWrapper Error:', error, errorInfo);
          // Report to error tracking service
          if (typeof window !== 'undefined' && 'gtag' in window) {
            (window as any).gtag('event', 'exception', {
              description: error?.message || 'Unknown error',
              fatal: false,
            });
          }
        } catch (loggingError) {
          console.error('Error in LazyWrapper error handler:', loggingError);
        }
      }}
    >
      <Suspense fallback={<MinLoadingTime fallback={fallback} minTime={minLoadingTime} />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
};

// Component to ensure minimum loading time for better UX
const MinLoadingTime: React.FC<{ fallback: React.ReactNode; minTime: number }> = ({ 
  fallback, 
  minTime 
}) => {
  const [showFallback, setShowFallback] = React.useState(true);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      startTransition(() => {
        setShowFallback(false);
      });
    }, minTime);

    return () => clearTimeout(timer);
  }, [minTime]);

  if (showFallback) {
    return <>{fallback}</>;
  }

  return null;
};

export default LazyWrapper;